<template>
  <BaseModal :open="open" maxWidth="480px" :closeOnBackdrop="false" @close="$emit('close')">
    <div ref="tagModal"
         class="v2026-tagm"
         role="dialog"
         aria-modal="true"
         aria-labelledby="v2026-tagm-title"
         @keydown.tab="trapTab">
      <div class="v2026-tagm__head">
        <span id="v2026-tagm-title" class="v2026-tagm__title">
          {{ system ? 'เพิ่มหัวข้อสนทนา' : 'เพิ่มแท็ก' }}
        </span>
        <button type="button" class="v2026-tagm__x" aria-label="ปิด" @click="$emit('close')">×</button>
      </div>

      <!-- BaseModal's dialog is max-height:90vh / overflow:hidden. With the Sketch disclosure open
           this body is ~640px, so without its own scroller the footer clips and บันทึก becomes
           unreachable. -->
      <div class="v2026-tagm__body">
        <template v-if="system">
          <div class="v2026-tagm__field">
            <span id="v2026-tag-kind-lbl" class="v2026-tagm__label">ประเภทหัวข้อ</span>
            <div class="v2026-tagm__radios"
                 role="radiogroup"
                 aria-labelledby="v2026-tag-kind-lbl"
                 :aria-describedby="noParents ? 'v2026-tag-kind-hint' : null">
              <label class="v2026-tagm__radio">
                <input ref="kindFirst" type="radio" name="v2026-tag-kind" :value="0" v-model="hasParent">
                <span>หัวข้อหลัก</span>
              </label>
              <label class="v2026-tagm__radio" :class="{ 'is-disabled': noParents }">
                <input type="radio" name="v2026-tag-kind" :value="1" :disabled="noParents" v-model="hasParent">
                <span>หัวข้อรอง</span>
              </label>
            </div>
            <div v-if="noParents" id="v2026-tag-kind-hint" class="v2026-tagm__hint">
              ยังไม่มีหัวข้อหลักให้เลือก — เพิ่มหัวข้อหลักก่อน
            </div>
          </div>

          <div v-if="hasParent" class="v2026-tagm__field">
            <label class="v2026-tagm__label" for="v2026-tag-parent">
              หัวข้อสนทนาหลัก<span class="v2026-tagm__req" aria-hidden="true"> *</span>
            </label>
            <select id="v2026-tag-parent"
                    ref="parentSelect"
                    class="v2026-tagm__select"
                    :class="{ 'is-invalid': !!errors.parent }"
                    :aria-invalid="errors.parent ? 'true' : 'false'"
                    :aria-describedby="errors.parent ? 'v2026-tag-parent-err' : null"
                    v-model="parentId"
                    @change="errors.parent = ''">
              <option :value="null">เลือกหัวข้อสนทนาหลัก</option>
              <!-- Keyed by the option's own id, not the loop index over the unfiltered list. -->
              <option v-for="option in parentOptions"
                      :key="option.id"
                      :value="option.id"
                      :style="{ color: option.color }">{{ option.name }}</option>
            </select>
            <div v-if="errors.parent" id="v2026-tag-parent-err" class="v2026-tagm__err">{{ errors.parent }}</div>
          </div>
        </template>

        <div class="v2026-tagm__field">
          <div class="v2026-tagm__labelrow">
            <label class="v2026-tagm__label" for="v2026-tag-name">
              {{ system ? 'ชื่อหัวข้อสนทนา (ไม่เกิน 20 ตัวอักษร)' : 'ชื่อแท็ก (ไม่เกิน 20 ตัวอักษร)' }}
              <span class="v2026-tagm__req" aria-hidden="true"> *</span>
            </label>
            <span class="v2026-tagm__count" :class="{ 'is-full': nameLength >= 20 }" aria-hidden="true">
              {{ nameLength }}/20
            </span>
          </div>
          <input id="v2026-tag-name"
                 ref="nameInput"
                 type="text"
                 maxlength="20"
                 class="v2026-tagm__input"
                 :class="{ 'is-invalid': !!errors.name }"
                 :aria-invalid="errors.name ? 'true' : 'false'"
                 :aria-describedby="errors.name ? 'v2026-tag-name-err' : null"
                 v-model="form.name"
                 @input="errors.name = ''"
                 @keyup.enter="submit">
          <div v-if="errors.name" id="v2026-tag-name-err" class="v2026-tagm__err">{{ errors.name }}</div>
        </div>

        <div class="v2026-tagm__field" role="group" aria-labelledby="v2026-tag-color-lbl">
          <!-- A plain span, not a <label>: a swatch grid has no single labelable control. -->
          <span id="v2026-tag-color-lbl" class="v2026-tagm__label">สี</span>

          <div class="v2026-tagm__preview">
            <span class="v2026-tagm__chip"
                  :style="{ backgroundColor: resolvedColor, color: glyphOn(resolvedColor) }"
                  aria-hidden="true">
              <span v-if="system">#</span>
              <span v-else class="v2026-tagm__chip-ico"></span>
            </span>
            <span class="v2026-tagm__hex">{{ resolvedColor }}</span>
          </div>
          <!-- vue-color's alpha slider is inert: Sketch returns a 6-digit hex and the 8-digit form
               lives in a separate key we never read. Say so rather than pretend it works. -->
          <div v-if="alphaIgnored" class="v2026-tagm__hint">ค่าความโปร่งใสจะไม่ถูกบันทึก</div>

          <div class="v2026-tagm__swatches">
            <button v-for="preset in presets"
                    :key="preset.hex"
                    type="button"
                    class="v2026-tagm__swatch"
                    :class="{ 'is-on': isPreset(preset.hex) }"
                    :style="{ backgroundColor: preset.hex }"
                    :aria-pressed="isPreset(preset.hex) ? 'true' : 'false'"
                    :aria-label="preset.name"
                    @click="pickPreset(preset.hex)"></button>
          </div>

          <button type="button"
                  class="v2026-tagm__disclose"
                  :aria-expanded="showPicker ? 'true' : 'false'"
                  aria-controls="v2026-tag-picker"
                  @click="showPicker = !showPicker">
            สีอื่น<span class="v2026-tagm__caret" :class="{ 'is-open': showPicker }" aria-hidden="true"></span>
          </button>

          <div v-if="showPicker" id="v2026-tag-picker" class="v2026-tagm__picker">
            <photoshop-picker v-model="colors"></photoshop-picker>
          </div>
        </div>

        <div v-if="system" class="v2026-tagm__check">
          <div class="v2026-tagm__checkrow">
            <input id="v2026-tag-canselect"
                   type="checkbox"
                   v-model="form.can_select"
                   :true-value="1"
                   :false-value="0"
                   aria-describedby="v2026-tag-canselect-note">
            <label for="v2026-tag-canselect">สามารถกดเลือกหัวข้อได้</label>
          </div>
          <div id="v2026-tag-canselect-note" class="v2026-tagm__note">
            หัวข้อที่กดเลือกได้
            <ul>
              <li>หัวข้อรองทุกข้อ</li>
              <li>หัวข้อหลักที่ไม่มีหัวข้อรอง</li>
            </ul>
          </div>
        </div>

        <div v-else class="v2026-tagm__field">
          <label class="v2026-tagm__label" for="v2026-tag-type">กลุ่มของแท็ก</label>
          <select id="v2026-tag-type" class="v2026-tagm__select" v-model="form.customer_type">
            <option v-for="option in typeOptions" :key="option.type" :value="option.type">{{ option.label }}</option>
          </select>
        </div>
      </div>

      <div class="v2026-tagm__foot">
        <button type="button" class="v2026-tagm__btn-ghost" @click="$emit('close')">ยกเลิก</button>
        <button type="button"
                class="v2026-tagm__btn-primary"
                :disabled="saving"
                @click="submit">{{ saving ? 'กำลังโหลด...' : 'บันทึก' }}</button>
      </div>
    </div>
  </BaseModal>
</template>

<script>
import { Sketch } from 'vue-color'
import BaseModal from '../coversation/BaseModal'

// version2026 preset palette. Seven of these are already in circulation — six are vue-color
// Sketch's own preset swatches (the only clickable colours this dialog has ever offered) and
// #1976D2 is the picker's initial value, i.e. the colour of every tag created without touching it.
// Five are v2026 fills for the hues whose legacy equivalents fail 4.5:1 under a white glyph.
// All opaque 6-digit hex, all >= 4.5:1 against #FFFFFF. Do NOT add a colour without measuring it.
const TAG_PRESETS = [
  { hex: '#D0021B', name: 'สีแดง' },
  { hex: '#C2410C', name: 'สีส้มเข้ม' },
  { hex: '#8B572A', name: 'สีน้ำตาล' },
  { hex: '#417505', name: 'สีเขียวมะกอก' },
  { hex: '#15794F', name: 'สีเขียวมรกต' },
  { hex: '#0B7285', name: 'สีฟ้าอมเขียว' },
  { hex: '#1976D2', name: 'สีน้ำเงิน' },
  { hex: '#9013FE', name: 'สีม่วง' },
  { hex: '#BD10E0', name: 'สีม่วงแดง' },
  { hex: '#BE185D', name: 'สีชมพูเข้ม' },
  { hex: '#546E7A', name: 'สีเทาน้ำเงิน' },
  { hex: '#4A4A4A', name: 'สีเทาเข้ม' }
]

const DEFAULT_COLOR = '#1976D2'

export default {
  name: 'tag-form-modal-v2026',
  components: {
    'photoshop-picker': Sketch,
    BaseModal
  },
  props: {
    open: { type: Boolean, default: false },
    system: { type: Boolean, default: false },
    parentOptions: { type: Array, default: () => [] },
    typeOptions: { type: Array, default: () => [] },
    defaultCustomerType: { type: String, default: 'customer' },
    saving: { type: Boolean, default: false }
  },
  data () {
    return {
      presets: TAG_PRESETS,
      // Own form state. The parent's tag / colors / parentId / hasParent belong to the flag-OFF
      // b-modal and are never read or written here, which is what makes the cross-mode leaks
      // structurally impossible rather than merely fixed.
      form: {
        name: '',
        can_select: 0,
        customer_type: this.defaultCustomerType
      },
      // The STRING, same shape as the legacy initial value: validateAddingTag's
      // (colors.hex) ? colors.hex : colors fallback depends on presets staying strings.
      colors: DEFAULT_COLOR,
      parentId: null,
      hasParent: 0,
      showPicker: false,
      errors: {
        name: '',
        parent: ''
      }
    }
  },
  computed: {
    nameLength () {
      return String(this.form.name || '').length
    },
    noParents () {
      return this.parentOptions.length === 0
    },
    resolvedColor () {
      return (this.colors.hex) ? this.colors.hex : this.colors
    },
    alphaIgnored () {
      return !!this.colors && typeof this.colors === 'object' && Number(this.colors.a) < 1
    }
  },
  watch: {
    open (isOpen) {
      if (!isOpen) return
      this.resetForm()
      this.$nextTick(() => {
        const el = this.system ? this.$refs.kindFirst : this.$refs.nameInput
        if (el) el.focus()
      })
    }
  },
  mounted () {
    if (this.open) this.resetForm()
  },
  methods: {
    resetForm () {
      this.form = {
        name: '',
        can_select: 0,
        // System topics live under customer_type = 'customer'; anything else makes the new topic
        // invisible in mode B forever.
        customer_type: this.system ? 'customer' : this.defaultCustomerType
      }
      this.colors = DEFAULT_COLOR
      this.parentId = null
      this.hasParent = 0
      this.showPicker = false
      this.errors = { name: '', parent: '' }
    },
    isPreset (hex) {
      return String(this.resolvedColor || '').toUpperCase() === hex.toUpperCase()
    },
    // A plain string, never an object: this keeps the object-or-string fallback intact.
    pickPreset (hex) {
      this.colors = hex
    },
    glyphOn (hex) {
      return this.relativeLuminance(hex) > 0.215 ? '#212529' : '#ffffff'
    },
    relativeLuminance (hex) {
      const raw = String(hex || '').trim().replace('#', '')
      let full = raw
      if (raw.length === 3) full = raw[0] + raw[0] + raw[1] + raw[1] + raw[2] + raw[2]
      if (full.length !== 6 || !/^[0-9a-fA-F]{6}$/.test(full)) return 0
      const channel = (v) => {
        const c = parseInt(v, 16) / 255
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
      }
      return 0.2126 * channel(full.slice(0, 2)) +
        0.7152 * channel(full.slice(2, 4)) +
        0.0722 * channel(full.slice(4, 6))
    },
    submit () {
      if (this.saving) return
      this.errors = { name: '', parent: '' }

      const name = String(this.form.name || '').trim()
      const needsParent = this.system && this.hasParent === 1 && !this.parentId
      if (needsParent) this.errors.parent = 'กรุณาเลือกหัวข้อสนทนาหลัก'
      if (!name) this.errors.name = this.system ? 'กรุณาระบุชื่อหัวข้อสนทนา' : 'กรุณาระบุชื่อแท็ก'
      if (this.errors.parent || this.errors.name) {
        // First invalid control in DOM order: the parent select sits above the name input.
        this.$nextTick(() => {
          const el = this.errors.parent ? this.$refs.parentSelect : this.$refs.nameInput
          if (el) el.focus()
        })
        return
      }

      const payload = {
        // The # prefix is applied to the value that is sent, never mid-typing.
        name: this.system && name.charAt(0) !== '#' ? '#' + name : name,
        color: this.resolvedColor,
        customer_type: this.system ? 'customer' : this.form.customer_type,
        is_line_audience_group: 0,
        line_audience_group_id: null,
        can_select: this.system ? this.form.can_select : 0,
        position: 0,
        system: this.system ? 1 : 0
      }
      if (this.parentId) payload.parent_id = this.parentId

      this.$emit('submit', payload)
    },
    // BaseModal does not trap focus, and this dialog is modal.
    trapTab (e) {
      const root = this.$refs.tagModal
      if (!root) return
      const sel = 'button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
      const items = Array.prototype.slice.call(root.querySelectorAll(sel))
      if (!items.length) return
      const first = items[0]
      const last = items[items.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
  }
}
</script>

<style lang="scss" scoped>

/* Only the ten --v2026-color-* tokens declared on .v2026-shell resolve under /settings, so every
   grey below is hard-coded on purpose. */
.v2026-tagm {
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  background: #ffffff;
  border-radius: 24px;
  overflow: hidden;
}

.v2026-tagm__head {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 10px;
  height: 56px;
  padding: 0 20px;
  border-bottom: 1px solid #e9ecef;
}

.v2026-tagm__title {
  font-size: 15px;
  font-weight: 600;
  color: #212529;
}

.v2026-tagm__x {
  margin-left: auto;
  width: 28px;
  height: 28px;
  border: 0;
  border-radius: 6px;
  background: none;
  font-size: 18px;
  line-height: 1;
  color: #66696C;
  cursor: pointer;

  &:hover { background: var(--v2026-color-hover-bg); color: #212529; }
  &:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(49, 193, 215, .30); }
}

/* 120 = head 56 + foot 64. Without this the Sketch disclosure pushes the footer past 90vh and
   the save button becomes unreachable. */
.v2026-tagm__body {
  flex: 1 1 auto;
  padding: 20px;
  overflow-y: auto;
  max-height: calc(90vh - 120px);
}

.v2026-tagm__field {
  margin-bottom: 16px;

  &:last-child { margin-bottom: 0; }
}

.v2026-tagm__labelrow {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
}

.v2026-tagm__label {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #343a40;
}

.v2026-tagm__req { color: #E5484D; }

.v2026-tagm__count {
  font-size: 12px;
  color: #66696C;

  &.is-full { color: #212529; }
}

.v2026-tagm__hint {
  margin-top: 6px;
  font-size: 12px;
  color: #66696C;
}

.v2026-tagm__radios {
  display: flex;
  align-items: center;
  gap: 20px;
}

.v2026-tagm__radio {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 14px;
  color: #212529;
  cursor: pointer;

  &.is-disabled { color: #98A2B3; cursor: default; }
  input { margin: 0; }
}

.v2026-tagm__input,
.v2026-tagm__select {
  width: 100%;
  height: 36px;
  padding: 0 12px;
  border: 1px solid #E4E7EC;
  border-radius: 8px;
  background: #ffffff;
  font-size: 14px;
  color: #212529;

  &:focus {
    outline: none;
    border-color: var(--v2026-color-main);
    box-shadow: 0 0 0 3px rgba(49, 193, 215, .30);
  }
  &.is-invalid {
    border-color: #E5484D;

    &:focus { border-color: #E5484D; box-shadow: 0 0 0 3px rgba(229, 72, 77, .20); }
  }
}

.v2026-tagm__err {
  margin-top: 6px;
  font-size: 12px;
  color: #E5484D;
}

.v2026-tagm__preview {
  display: flex;
  align-items: center;
  gap: 12px;
}

.v2026-tagm__chip {
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  line-height: 1;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, .08);
}

.v2026-tagm__chip-ico {
  width: 18px;
  height: 18px;
  background-color: currentColor;
  -webkit-mask: url('~@/assets/icons2026/tag.svg') center / contain no-repeat;
          mask: url('~@/assets/icons2026/tag.svg') center / contain no-repeat;
}

.v2026-tagm__hex {
  font-size: 13px;
  color: #66696C;
}

.v2026-tagm__swatches {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
  /* 6 * 32 + 5 * 8. flex-wrap then degrades to 4 per row under ~300px with no media query. */
  max-width: 232px;
}

.v2026-tagm__swatch {
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 8px;
  cursor: pointer;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, .10);

  /* Selection is a near-black ring on a white gap, never colour alone. */
  &.is-on { box-shadow: inset 0 0 0 1px rgba(0, 0, 0, .10), 0 0 0 2px #fff, 0 0 0 4px #212529; }
  &:focus-visible {
    outline: none;
    box-shadow: inset 0 0 0 1px rgba(0, 0, 0, .10), 0 0 0 3px rgba(49, 193, 215, .30);
  }
}

.v2026-tagm__disclose {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  margin-top: 12px;
  padding: 0 12px;
  border: 1px solid #E4E7EC;
  border-radius: 8px;
  background: #ffffff;
  font-size: 13px;
  color: #212529;
  cursor: pointer;

  &:hover { background: var(--v2026-color-hover-bg); }
  &:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(49, 193, 215, .30); }
}

.v2026-tagm__caret {
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 5px solid #66696C;
  transition: transform 150ms ease;

  &.is-open { transform: rotate(180deg); }
}

/* The Sketch panel keeps its own visual language; it is behind a disclosure precisely so it is
   not the default view. Do not restyle its internals. */
.v2026-tagm__picker { margin-top: 12px; }

.v2026-tagm__check {
  padding: 12px;
  border: 1px solid #E4E7EC;
  border-radius: 8px;
}

.v2026-tagm__checkrow {
  display: flex;
  align-items: center;
  gap: 8px;

  label {
    margin: 0;
    font-size: 14px;
    color: #212529;
    cursor: pointer;
  }
}

.v2026-tagm__note {
  margin-top: 6px;
  font-size: 13px;
  color: #66696C;

  ul {
    margin: 4px 0 0;
    padding-left: 20px;
  }
}

.v2026-tagm__foot {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  height: 64px;
  padding: 0 20px;
  border-top: 1px solid #e9ecef;
}

.v2026-tagm__btn-primary {
  height: 36px;
  padding: 0 14px;
  border: 0;
  border-radius: 8px;
  background: var(--v2026-color-main);
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) { filter: brightness(.94); }
  &:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(49, 193, 215, .30); }
  &:disabled { opacity: .5; cursor: default; }
}

.v2026-tagm__btn-ghost {
  height: 36px;
  padding: 0 14px;
  border: 1px solid #E4E7EC;
  border-radius: 8px;
  background: #ffffff;
  font-size: 14px;
  color: #212529;
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) { background: var(--v2026-color-hover-bg); }
  &:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(49, 193, 215, .30); }
}

@media (prefers-reduced-motion: reduce) {
  .v2026-tagm__caret { transition: none; }
}
</style>
